import React, { useEffect, useRef, useState } from 'react'
import Select from 'react-select'
import { KTIcon } from '../../../../../../_metronic/helpers'
import { useThemeMode } from '../../../../../../_metronic/partials'
import { SelectStyles } from '../../../../../../components/Select'
import { SmartTooltip } from '../../../../../../components/SmartTooltip/SmartTooltip'
import { ISelectOption } from '../../../../../common/types/select'
import {
  DefaultHint,
  RangeSlider,
  sliderValueLabel,
} from '../../../../../../components/RangeSlider/RangeSlider'
import { FieldDescriptor } from '../core/types'

// Project select theme + a menu portal so the dropdown floats above the modal
// instead of pushing its content.
export const themedSelectStyles = (theme: 'light' | 'dark' | 'system') => ({
  ...(SelectStyles(theme) as any),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
})

interface Props {
  field: FieldDescriptor
  value: unknown
  onChange: (key: string, value: unknown) => void
  merchantOptions: ISelectOption<number>[]
  /** Справочник банков для полей `bank_list` — значение опции это код банка. */
  bankOptions?: ISelectOption<string>[]
  /** Справочник платёжных методов для полей `method_list` — значение опции это id метода. */
  methodOptions?: ISelectOption<number>[]
  error?: string
  /** Set when the field is meaningless in the current state; the reason is shown under it. */
  disabledReason?: string
}

// FieldLabel is two lines: the raw parameter code + an info icon (tooltip with the
// full description) on top, the readable Russian name underneath.
const FieldLabel: React.FC<{ field: FieldDescriptor }> = ({ field }) => (
  <div className='mb-2'>
    <div className='d-flex align-items-center gap-2'>
      <span
        className='fw-semibold text-gray-900'
        style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
      >
        {field.key}
      </span>
      {field.description && (
        <SmartTooltip content={field.description} maxWidth={300}>
          <span className='text-gray-500 d-inline-flex' style={{ cursor: 'help' }}>
            <KTIcon iconName='information-2' className='fs-6' />
          </span>
        </SmartTooltip>
      )}
    </div>
    {field.label && <div className='text-gray-700 fs-8'>{field.label}</div>}
  </div>
)

// digits only; float/amount also allow a single decimal point
const INT_RE = /^\d*$/
const NUM_RE = /^\d*\.?\d*$/

// NumberInput keeps a raw text buffer for the same reason FloatListInput does: a
// controlled input that re-derives its text from the parsed number cannot be typed
// into. `1.` parses to 1, the input redraws as "1", and the decimal point is eaten
// on every keystroke — AUTO_HALTED_TIMEOUT accepted 1.5 through the API but was
// unenterable in the form (CPAYMENT-1723). The buffer only resyncs when the parent
// value changes to something the buffer does not already represent (external load),
// so trailing "." and "1.50" survive while typing.
const NumberInput: React.FC<{
  kind: 'int' | 'float' | 'amount_string'
  value: unknown
  invalid: string
  disabled?: boolean
  onChange: (v: unknown) => void
}> = ({ kind, value, invalid, disabled, onChange }) => {
  const toText = (v: unknown) => (v === undefined || v === null ? '' : String(v))
  const [raw, setRaw] = useState(() => toText(value))
  const lastEmitted = useRef<unknown>(value)

  useEffect(() => {
    if (value !== lastEmitted.current) {
      setRaw(toText(value))
      lastEmitted.current = value
    }
  }, [value])

  const re = kind === 'int' ? INT_RE : NUM_RE

  return (
    <input
      className={`form-control form-control-sm${invalid}`}
      type='text'
      inputMode='decimal'
      disabled={disabled}
      value={raw}
      onChange={(e) => {
        const text = e.target.value
        if (!re.test(text)) return
        setRaw(text)
        let next: unknown
        if (text === '' || text === '.') next = undefined
        else if (kind === 'amount_string') next = text
        else next = kind === 'int' ? parseInt(text, 10) : parseFloat(text)
        lastEmitted.current = next
        onChange(next)
      }}
    />
  )
}

// FloatListInput keeps a raw text buffer so the user can freely type a
// comma-separated list (e.g. "1000, 2500, 5000") without the controlled input
// re-deriving the text from the parsed array and eating separators. The parsed
// number[] is emitted to the parent; the buffer only resyncs on external loads.
const FloatListInput: React.FC<{
  value: unknown
  invalid: string
  disabled?: boolean
  onChange: (v: unknown) => void
}> = ({ value, invalid, disabled, onChange }) => {
  const toText = (v: unknown) => (Array.isArray(v) ? (v as number[]).join(', ') : '')
  const [raw, setRaw] = useState(() => toText(value))
  const lastEmitted = useRef<number[] | undefined>(
    Array.isArray(value) ? (value as number[]) : undefined
  )

  useEffect(() => {
    const arr = Array.isArray(value) ? (value as number[]) : undefined
    if (JSON.stringify(arr) !== JSON.stringify(lastEmitted.current)) {
      setRaw(arr ? arr.join(', ') : '')
      lastEmitted.current = arr
    }
  }, [value])

  return (
    <input
      className={`form-control form-control-sm${invalid}`}
      type='text'
      placeholder='например: 1000, 2500, 5000'
      disabled={disabled}
      value={raw}
      onChange={(e) => {
        const t = e.target.value
        setRaw(t)
        const arr = t
          .split(',')
          .map((s) => parseFloat(s.trim()))
          .filter((n) => !Number.isNaN(n))
        lastEmitted.current = arr.length ? arr : undefined
        onChange(arr.length ? arr : undefined)
      }}
    />
  )
}

// defaultText is the catalog default in the words the field shows it; lists have no hint.
const defaultText = (field: FieldDescriptor): string | null => {
  const d = field.default
  if (d === undefined || d === null || Array.isArray(d)) return null
  if (field.type === 'bool') return d === true ? 'Включено' : 'Выключено'
  if (field.type === 'choice') return field.options?.find((o) => o.value === d)?.label ?? String(d)
  if (field.type === 'slider' && field.range && typeof d === 'number')
    return sliderValueLabel(field.range, d)
  if (typeof d === 'object') return null
  return String(d)
}

export const SchemaField: React.FC<Props> = ({
  field,
  value,
  onChange,
  merchantOptions,
  bankOptions = [],
  methodOptions = [],
  error,
  disabledReason,
}) => {
  const { mode } = useThemeMode()
  const invalid = error ? ' is-invalid' : ''
  const disabled = !!disabledReason
  const set = (v: unknown) => onChange(field.key, v)

  const renderInput = () => {
    switch (field.type) {
      case 'int':
      case 'float':
      case 'amount_string':
        return (
          <NumberInput
            kind={field.type}
            value={value}
            invalid={invalid}
            disabled={disabled}
            onChange={set}
          />
        )
      // bool — переключатель. Выключённое состояние не хранится: наружу уходит
      // undefined, и ProfileFormModal убирает ключ из config, оставляя значение на
      // дефолте каталога. Так «выключено» и «не задано» — одно и то же, и в JSONB
      // не копятся false-ы (CPAYMENT-1851).
      case 'bool': {
        const checked = value === true
        return (
          <div className='form-check form-switch form-check-custom form-check-solid'>
            <input
              className='form-check-input'
              type='checkbox'
              id={`schema-field-${field.key}`}
              disabled={disabled}
              checked={checked}
              onChange={(e) => set(e.target.checked ? true : undefined)}
            />
            <label
              className='form-check-label text-gray-700 fs-8'
              htmlFor={`schema-field-${field.key}`}
            >
              {checked ? 'Включено' : 'Выключено'}
            </label>
          </div>
        )
      }
      case 'choice':
        return (
          <div className='d-flex flex-column gap-2'>
            {(field.options ?? []).map((o) => {
              const id = `schema-field-${field.key}-${o.value}`
              return (
                <div className='form-check form-check-custom form-check-solid form-check-sm' key={o.value}>
                  <input
                    className='form-check-input'
                    type='radio'
                    id={id}
                    name={`schema-field-${field.key}`}
                    disabled={disabled}
                    checked={value === o.value}
                    onChange={() => set(o.value)}
                  />
                  <label className='form-check-label text-gray-700 fs-7' htmlFor={id}>
                    {o.label}
                  </label>
                </div>
              )
            })}
          </div>
        )
      case 'string':
        return (
          <input
            className={`form-control form-control-sm${invalid}`}
            type='text'
            disabled={disabled}
            value={(value as string) ?? ''}
            onChange={(e) => set(e.target.value === '' ? undefined : e.target.value)}
          />
        )
      case 'int_list': {
        const ids = Array.isArray(value) ? (value as number[]) : []
        const selected = merchantOptions.filter((o) => ids.includes(o.value))
        return (
          <Select
            isMulti
            isDisabled={disabled}
            options={merchantOptions}
            value={selected}
            classNamePrefix='react-select'
            placeholder='Выберите мерчантов…'
            noOptionsMessage={() => 'Нет мерчантов'}
            menuPortalTarget={document.body}
            menuPosition='fixed'
            styles={themedSelectStyles(mode)}
            onChange={(opts) => set((opts as ISelectOption<number>[]).map((o) => o.value))}
          />
        )
      }
      // bank_list — коды банков из справочника. Наружу уходит только код: матчинг
      // сравнивает с ним банк реквизита выплаты (CPAYMENT-1748).
      case 'bank_list': {
        const codes = Array.isArray(value) ? (value as string[]) : []
        const selectedBanks = bankOptions.filter((o) => codes.includes(o.value))
        return (
          <Select
            isMulti
            isDisabled={disabled}
            options={bankOptions}
            value={selectedBanks}
            classNamePrefix='react-select'
            placeholder='Выберите банки…'
            noOptionsMessage={() => 'Нет банков'}
            menuPortalTarget={document.body}
            menuPosition='fixed'
            styles={themedSelectStyles(mode)}
            onChange={(opts) => set((opts as ISelectOption<string>[]).map((o) => o.value))}
          />
        )
      }
      // method_list — платёжные методы из справочника. Наружу уходит только id: матчинг
      // сравнивает с ним метод тикета — выплаты на PAYOUT, депозита на PAYMENT (CPAYMENT-1695).
      case 'method_list': {
        const ids = Array.isArray(value) ? (value as number[]) : []
        const selectedMethods = methodOptions.filter((o) => ids.includes(o.value))
        return (
          <Select
            isMulti
            isDisabled={disabled}
            options={methodOptions}
            value={selectedMethods}
            classNamePrefix='react-select'
            placeholder='Выберите платёжные методы…'
            noOptionsMessage={() => 'Нет методов'}
            menuPortalTarget={document.body}
            menuPosition='fixed'
            styles={themedSelectStyles(mode)}
            onChange={(opts) => set((opts as ISelectOption<number>[]).map((o) => o.value))}
          />
        )
      }
      case 'slider': {
        if (!field.range) return null
        const r = field.range
        const fallback = r.off ?? r.min
        const current = typeof value === 'number' ? value : fallback
        return (
          <RangeSlider
            range={r}
            value={current}
            disabled={disabled}
            defaultValue={typeof field.default === 'number' ? field.default : null}
            onChange={(v) => set(r.off != null && v === r.off ? undefined : v)}
          />
        )
      }
      case 'float_list':
        return <FloatListInput value={value} invalid={invalid} disabled={disabled} onChange={set} />

      default:
        return (
          <input
            className={`form-control form-control-sm${invalid}`}
            type='text'
            disabled={disabled}
            value={(value as string) ?? ''}
            onChange={(e) => set(e.target.value)}
          />
        )
    }
  }

  return (
    <div>
      <FieldLabel field={field} />
      {renderInput()}
      <DefaultHint text={defaultText(field)} />
      {disabledReason && <div className='text-gray-500 fs-8 mt-1'>{disabledReason}</div>}
      {error && <div className='text-danger fs-8 mt-1'>{error}</div>}
    </div>
  )
}
